import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Paper, FormControlLabel, Checkbox, Snackbar, Alert, CircularProgress } from "@mui/material";
import { Icon } from "@iconify/react";
import DomainVerificationPopup from "../components/DomainVerification";
import { postDataToAPI } from "../services/ApiService";

function AttackSurfaceMapper() {
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState("");
  const [domainVerified, setDomainVerified] = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const siteKey = "6Ld8IcIqAAAAAOSN4Isuy7Udbt8VFzTe-5y0vD-D"; // Replace with your reCAPTCHA v2 key

  const validateFields = () => {
    const fieldErrors = {};
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      fieldErrors.email = "Please enter a valid business email address.";
    }
    if (!url || !/^https?:\/\/.+/.test(url)) {
      fieldErrors.url = "Please enter a valid URL starting with http:// or https://.";
    }
    if (!domainVerified) {
      fieldErrors.domainVerification = "Domain verification is required.";
    }
    if (!tosChecked) {
      fieldErrors.tos = "You must agree to the Terms of Service.";
    }
    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  const handleCaptchaSuccess = (token) => {
    setCaptchaToken(token);
    setIsSubmitDisabled(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;
    let payload = {
      'email': email,
      'url': url,
      'reCaptchaToken': captchaToken
    };

    const result = await postDataToAPI('attacksurfacemapper', payload, setLoading, setToast);
    if (result) setFormSubmitted(true);
    console.log(
      `Attack Surface Mapper Submitted:\nEmail: ${email}\nURL: ${url}\nDomain Verified: ${domainVerified}\nTOS Accepted: ${tosChecked}\nCaptcha Token: ${captchaToken}`
    );
    setFormSubmitted(true);
  };

  useEffect(() => {
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => {
        window.grecaptcha.render("captcha-container", {
          sitekey: siteKey,
          callback: handleCaptchaSuccess,
        });
      });
    }
  }, []);

  function isBusinessEmailFormat(email, url) {
    if (!email || !url) return false;

    // Public email providers to block
    const PUBLIC_DOMAINS = [
      "gmail.com",
      "yahoo.com",
      "hotmail.com",
      "outlook.com",
      "live.com",
      "icloud.com"
    ];

    // Extract email domain
    const emailDomain = email.split("@")[1]?.toLowerCase();
    if (!emailDomain) return false;

    // Block public email domains
    if (PUBLIC_DOMAINS.includes(emailDomain)) return false;

    let urlDomain;
    try {
      const parsedUrl = new URL(url);
      urlDomain = parsedUrl.hostname.toLowerCase().replace(/^www\./, "");
    } catch {
      return false; // invalid URL
    }

    // Normalize email domain
    const normalizedEmailDomain = emailDomain.replace(/^www\./, "");

    // Match exact domain or subdomain
    return (
      urlDomain === normalizedEmailDomain ||
      urlDomain.endsWith(`.${normalizedEmailDomain}`) ||
      normalizedEmailDomain.endsWith(`.${urlDomain}`)
    );
  }

  const handleDomainVerification = () => {
    if(!email || !url) return setDomainVerified(false);
    const isValid = isBusinessEmailFormat(email, url);
    setDomainVerified(isValid);

    if (!isValid) {
      setErrors((prev) => ({
        ...prev,
        domainVerification:
          "Email domain must match website and cannot be a public email",
      }));
    } else {
      setErrors((prev) => {
        const { domainVerification, ...rest } = prev;
        return rest;
      });
    }
  };



  return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" bgcolor="#f9fafb" pt={2} px={0} sx={{ paddingBottom: "3rem" }}>
      {/* Header Section */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ color: "#000" }}>
          <Icon icon="ix:radarchart" width={45} height={45} style={{ marginRight: "16px", color: "rgb(0, 145, 70)" }} />
          Attack Surface Mapper
        </Typography>
        <Typography variant="body1" sx={{ color: "#333", maxWidth: 800, fontSize: "1.2rem" }}>
          This free online tool scans your digital infrastructure. See what all is visible externally.
        </Typography>
      </Box>

      {/* Form Section */}
      {!formSubmitted ? (
        <Box component="form" onSubmit={handleSubmit} sx={{ backgroundColor: "#fff", padding: 4, borderRadius: "8px", boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)", width: "100%", maxWidth: 800 }}>
          <TextField
            fullWidth
            label="Business Email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={handleDomainVerification}
            error={Boolean(errors.email)}
            helperText={errors.email}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "rgb(0, 145, 70)" } } }}
          />
          <TextField
            fullWidth
            label="Website URL"
            value={url}
            onBlur={handleDomainVerification}
            onChange={(e) => setUrl(e.target.value)}
            error={Boolean(errors.url)}
            helperText={errors.url}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "rgb(0, 145, 70)" } } }}
          />

          {/* Domain Verification Button */}
          {/* <Button
            variant="contained"
            onClick={handleDomainVerification}
            sx={{
              backgroundColor: domainVerified ? "green" : "red",
              color: "#fff",
              mb: 2,
              fontWeight: "bold",
            }}
          >
            {domainVerified ? "Domain Verified" : "Verify Domain"}
          </Button> */}
          {errors.domainVerification && <Typography variant="body2" color="error">{errors.domainVerification}</Typography>}
          <br />
          <FormControlLabel
            control={
              <Checkbox
                checked={tosChecked}
                onChange={(e) => setTosChecked(e.target.checked)}
                sx={{
                  color: "rgb(0, 145, 70)",
                  "&.Mui-checked": {
                    color: "rgb(0, 145, 70)",
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "#4b5563" }}>
                I have read and agree to the{" "}
                <a href="#" style={{ color: "rgb(0, 145, 70)", textDecoration: "none" }}>
                  Terms of Service
                </a>
                .
              </Typography>
            }
          />
          {errors.tos && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mb: 1, mt: -1, display: "block" }}
            >
              {errors.tos}
            </Typography>
          )}
          {/* CAPTCHA */}
          <div id="captcha-container" style={{ marginBottom: "16px", display: "block", width: "90%" }}></div>

          {/* Submit Button */}
          <Button type="submit" variant="contained" disabled={isSubmitDisabled || loading} sx={{
            mt: 2, backgroundColor: isSubmitDisabled ? "#ccc" : "rgb(0, 145, 70)",
            color: isSubmitDisabled ? "#888" : "#fff", padding: "12px 0",
            width: "300px", fontWeight: "bold",
            "&:hover": { backgroundColor: isSubmitDisabled ? "#ccc" : "rgb(0, 145, 70)" }
          }}>
            {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Submit"}
          </Button>
          {(toast.severity === 'error') && <Typography variant="body2" sx={{ my: 2, fontSize: '1.5rem', background: '#ba1111', color: '#fff', padding: '5px', borderRadius: '5px' }}>{toast.message} <IconButton sx={{ float: 'right', paddingTop: '5px', color: '#fff' }} onClick={() => setToast({})}><Icon icon="uil:times" width="24" height="24" /></IconButton></Typography>}
        </Box>
      ) : (
        <Box component="div" sx={{ backgroundColor: "#fff", padding: 4, borderRadius: "8px", boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)", width: "100%", maxWidth: 800 }}>
          <Typography variant="body1" sx={{ color: "#333", textAlign: "center", fontSize: "1.5rem" }}>
            Thank you. Your request has been submitted. You will be informed by email when the results are available.
          </Typography>
        </Box>
      )}

      {/* Domain Verification Modal */}
      <DomainVerificationPopup open={openDialog} onClose={() => setOpenDialog(false)} onVerify={() => { setDomainVerified(true); setOpenDialog(false); }} url={url} />

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AttackSurfaceMapper;
